import React, { useState, useMemo, useEffect } from "react";
import {
  Box,
  Paper,
  Typography,
  Container,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
} from "@mui/material";
import ReactECharts from "echarts-for-react";
import AssessmentOutlinedIcon from "@mui/icons-material/AssessmentOutlined";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import { getMonthlyStats } from "../../../services/dashboard";

export default function MonthlyReports() {
  const [totalClaims, setTotalClaims] = useState(0);
  const [averageClaims, setAverageClaims] = useState(0);
  // --- FILTER STATE ---
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth());
  const currentYear = new Date().getFullYear();

  const [claimsData, setClaimsData] = useState<number[]>([]);

  const months = useMemo(
    () => [
      "January",
      "February",
      "March",
      "April",
      "May",
      "June",
      "July",
      "August",
      "September",
      "October",
      "November",
      "December",
    ],
    [],
  );

  // --- LOGIC TO GET DAYS OF THE MONTH ---
  const { daysInMonth, monthName, daysCount } = useMemo(() => {
    const year = currentYear;
    const month = selectedMonth;

    const count = new Date(year, month + 1, 0).getDate();
    const daysArray = Array.from({ length: count }, (_, i) =>
      (i + 1).toString(),
    );
    const name = months[month];

    return { daysInMonth: daysArray, monthName: name, daysCount: count };
  }, [selectedMonth, currentYear, months]);

  useEffect(() => {
    let mounted = true;

    const fetchMonthlyStats = async () => {
      const response = await getMonthlyStats(currentYear, selectedMonth + 1);

      const payload = response?.data?.data ?? response?.data ?? {};

      const dailyData = payload?.dailyData ?? [];
      setClaimsData(payload.dailyData || []);
      setTotalClaims(payload.totalClaims || 0);
      setAverageClaims(payload.averageClaims || 0);

      if (mounted) {
        setClaimsData(dailyData);
      }
    };

    fetchMonthlyStats();

    return () => {
      mounted = false;
    };
  }, [currentYear, selectedMonth]);

  const handleMonthChange = (event: SelectChangeEvent<number>) => {
    setSelectedMonth(Number(event.target.value));
  };

  const option = {
    tooltip: {
      trigger: "axis",
      backgroundColor: "rgba(255, 255, 255, 0.9)",
      borderWidth: 1,
      borderColor: "#ccc",
      padding: 10,
      textStyle: { color: "#000" },
    },
    grid: {
      left: "3%",
      right: "4%",
      bottom: "5%",
      containLabel: true,
    },
    xAxis: {
      type: "category",
      boundaryGap: false,
      data: daysInMonth,
      axisLabel: {
        interval: daysCount > 15 ? 1 : 0,
        color: "#666",
        fontSize: 11,
      },
      axisLine: { lineStyle: { color: "#ddd" } },
    },
    yAxis: {
      type: "value",
      name: "Claims",
      splitLine: {
        lineStyle: {
          type: "dashed",
          color: "#E0E0E0",
        },
      },
    },
    series: [
      {
        name: "Claims Captured",
        data: claimsData,
        type: "line",
        smooth: true,
        symbol: "circle",
        symbolSize: 6,
        lineStyle: {
          width: 3,
          color: "#C0772D",
        },
        itemStyle: {
          color: "#C0772D",
        },
        areaStyle: {
          color: {
            type: "linear",
            x: 0,
            y: 0,
            x2: 0,
            y2: 1,
            colorStops: [
              { offset: 0, color: "rgba(192, 119, 45, 0.4)" },
              { offset: 1, color: "rgba(192, 119, 45, 0)" },
            ],
          },
        },
      },
    ],
  };

  return (
    <Box sx={{ display: "flex", minHeight: "100vh", bgcolor: "#F8F9FA" }}>
      <Box
        component="main"
        sx={{ flexGrow: 1, display: "flex", flexDirection: "column" }}
      >
        <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
          <Box
            sx={{
              mb: 3,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              flexWrap: "wrap",
              gap: 2,
            }}
          >
            <Box>
              <Typography
               
                         fontSize={{ xs: 18, sm: 22, md: 25 }}
                         fontWeight={600}
                         color="primary.main"
                         textAlign="left"
              >
                {monthName} Analytics
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Reviewing daily intake and processing averages.
              </Typography>
            </Box>

            <FormControl sx={{ minWidth: 200 }}>
              <InputLabel id="month-select-label">Filter by Month</InputLabel>
              <Select
                labelId="month-select-label"
                id="month-select"
                value={selectedMonth}
                label="Filter by Month"
                onChange={handleMonthChange}
                sx={{ borderRadius: "8px", bgcolor: "white" }}
              >
                {months.map((month, index) => (
                  <MenuItem key={month} value={index}>
                    {month}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          {/* STAT CARDS - Replaced Grid with Flex Box to avoid MUI Version Conflicts */}
          <Box
            sx={{
              display: "flex",
              gap: 3,
              mb: 4,
              flexDirection: { xs: "column", md: "row" },
            }}
          >
            <Paper
              sx={{
                p: 3,
                flex: 1,
                borderLeft: "5px solid #4CAF50",
                display: "flex",
                alignItems: "center",
                bgcolor:"#ffffff"
              }}
            >
              <Box sx={{ flexGrow: 1 }}>
                <Typography variant="h4" sx={{ fontWeight: "bold" }}>
                  {totalClaims.toLocaleString()}
                </Typography>
                <Typography color="textSecondary">
                  Total Claims Captured
                </Typography>
              </Box>
              <AssessmentOutlinedIcon
                sx={{ fontSize: 40, color: "#4CAF50", opacity: 0.5 }}
              />
            </Paper>

            <Paper
              sx={{
                p: 3,
                flex: 1,
                borderLeft: "5px solid #2196F3",
                display: "flex",
                alignItems: "center",
                bgcolor:"#ffffff"
              }}
            >
              <Box sx={{ flexGrow: 1 }}>
                <Typography variant="h4" sx={{ fontWeight: "bold" }}>
                  {averageClaims}
                </Typography>
                <Typography color="textSecondary">
                  Avg. Claims Per Day
                </Typography>
              </Box>
              <TrendingUpIcon
                sx={{ fontSize: 40, color: "#2196F3", opacity: 0.5 }}
              />
            </Paper>
          </Box>

          {/* CHART SECTION */}
          <Paper
            sx={{
              p: 4,
              borderRadius: "16px",
              boxShadow: "0px 4px 20px rgba(0,0,0,0.05)",
              bgcolor: "white",
            }}
          >
            <Typography variant="h6" sx={{ mb: 4, fontWeight: 600 }}>
              Claims Captured: {monthName} {currentYear}
            </Typography>

            <ReactECharts
              option={option}
              style={{ height: "450px", width: "100%" }}
              notMerge={true}
            />
          </Paper>
        </Container>
      </Box>
    </Box>
  );
}
